import { useState, useEffect, createContext, useContext, useRef } from 'react';
import { Routes, Route, Navigate, useNavigate, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Calendar, User, LogOut, Clock, Loader2, ChevronLeft, ChevronRight, Phone, Stethoscope, Users, FileText, Upload, Bell, Download, X, Check, XCircle, MessageCircle, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Toaster, toast } from 'sonner';
import { ClinicChat } from '../components/ClinicChat';

const API = process.env.REACT_APP_BACKEND_URL;

// ==================== AUTH CONTEXT ====================
const DoctorAuthContext = createContext(null);

function useDoctorAuth() {
  return useContext(DoctorAuthContext);
}

function DoctorAuthProvider({ children }) {
  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for impersonation token in URL
    const params = new URLSearchParams(window.location.search);
    const impToken = params.get('impersonate_token');
    if (impToken) {
      localStorage.setItem('doctor_token', impToken);
      localStorage.setItem('doctor_impersonated', 'true');
      window.history.replaceState({}, '', window.location.pathname);
    }

    const token = localStorage.getItem('doctor_token');
    if (token) {
      axios.get(`${API}/api/portal/doctor/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setDoctor(res.data))
        .catch(() => { localStorage.removeItem('doctor_token'); setDoctor(null); })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/api/portal/doctor/login`, { email, password });
    if (res.data.two_factor_required) {
      return res.data; // Return 2FA flag without setting token
    }
    localStorage.setItem('doctor_token', res.data.access_token);
    setDoctor(res.data.doctor);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem('doctor_token');
    setDoctor(null);
  };

  return (
    <DoctorAuthContext.Provider value={{ doctor, loading, login, logout, isAuthenticated: !!doctor }}>
      {children}
    </DoctorAuthContext.Provider>
  );
}

function doctorApi() {
  const token = localStorage.getItem('doctor_token');
  return axios.create({
    baseURL: `${API}/api/portal/doctor`,
    headers: { Authorization: `Bearer ${token}` }
  });
}

// ==================== COMPONENTS ====================
function DoctorShell({ children, title }) {
  const { doctor, logout } = useDoctorAuth();
  const location = useLocation();

  const nav = [
    { to: '/medico', icon: Calendar, label: 'Calendario' },
    { to: '/medico/ricette', icon: FileText, label: 'Ricette' },
    { to: '/medico/chat', icon: MessageCircle, label: 'Chat' },
    { to: '/medico/profilo', icon: User, label: 'Profilo' }
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col max-w-lg mx-auto" data-testid="doctor-shell">
      <header className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between sticky top-0 z-10">
        <div>
          <h1 className="font-semibold text-slate-800 text-lg" style={{ fontFamily: 'Manrope' }}>{title}</h1>
          {doctor && <p className="text-xs text-slate-500">Dr. {doctor.name}</p>}
        </div>
        {doctor && (
          <button onClick={logout} className="text-slate-400 hover:text-red-500 transition-colors" data-testid="doctor-logout-btn">
            <LogOut className="w-5 h-5" />
          </button>
        )}
      </header>
      <main className="flex-1 p-4">{children}</main>
      {doctor && (
        <DoctorNavBar nav={nav} />
      )}
    </div>
  );
}

// ==================== LOGIN ====================
function DoctorLoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showReset, setShowReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [twoFaRequired, setTwoFaRequired] = useState(false);
  const [twoFaEmail, setTwoFaEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const { login, isAuthenticated } = useDoctorAuth();
  const navigate = useNavigate();

  if (isAuthenticated) return <Navigate to="/medico" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await login(email, password);
      if (res?.two_factor_required) {
        setTwoFaRequired(true);
        setTwoFaEmail(res.email || email);
        setLoading(false);
        return;
      }
      navigate('/medico');
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Credenziali non valide');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2fa = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/portal/doctor/verify-2fa`, { email: twoFaEmail, code: otpCode });
      localStorage.setItem('doctor_token', res.data.access_token);
      window.location.href = '/medico';
    } catch (err) {
      setError(err.response?.data?.detail || 'Codice non valido');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-white flex items-center justify-center p-4">
      <div className="w-full max-w-sm" data-testid="doctor-login-page">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Stethoscope className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope' }}>Area Medico</h1>
          <p className="text-slate-500 text-sm mt-1">VoiceBook - Portale Medici</p>
        </div>

        {twoFaRequired ? (
          <form onSubmit={handleVerify2fa} className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-4">
            <div className="text-center mb-2">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Shield className="w-6 h-6 text-emerald-600" />
              </div>
              <h2 className="font-semibold text-slate-800">Verifica identita</h2>
              <p className="text-xs text-slate-500 mt-1">Codice inviato a {twoFaEmail}</p>
            </div>
            {error && <div className="bg-red-50 text-red-600 text-sm rounded-lg p-3">{error}</div>}
            <div>
              <Label>Codice di verifica</Label>
              <Input
                type="text" inputMode="numeric" maxLength={6}
                placeholder="000000" value={otpCode}
                onChange={e => setOtpCode(e.target.value.replace(/\D/g, ''))}
                className="mt-1.5 text-center text-2xl tracking-[0.3em] font-mono"
                autoFocus data-testid="doctor-otp-input"
              />
            </div>
            <Button type="submit" className="w-full bg-emerald-500 hover:bg-emerald-600 text-white" disabled={loading || otpCode.length < 6} data-testid="doctor-otp-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verifica'}
            </Button>
            <button type="button" onClick={() => { setTwoFaRequired(false); setOtpCode(''); setError(''); }}
              className="w-full text-xs text-slate-400 hover:text-slate-600">Torna al login</button>
          </form>
        ) : (
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-4">
          {error && <div className="bg-red-50 text-red-600 text-sm rounded-lg p-3" data-testid="doctor-login-error">{error}</div>}
          <div>
            <Label className="text-slate-700">Email</Label>
            <Input type="email" placeholder="dottore@email.com" value={email} onChange={e => setEmail(e.target.value)} required data-testid="doctor-email-input" />
          </div>
          <div>
            <Label className="text-slate-700">Password</Label>
            <Input type="password" value={password} onChange={e => setPassword(e.target.value)} required data-testid="doctor-password-input" />
          </div>
          <Button type="submit" className="w-full bg-emerald-500 hover:bg-emerald-600 text-white" disabled={loading} data-testid="doctor-login-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Accedi'}
          </Button>
        </form>
        )}

        <div className="text-center mt-3 space-y-2">
          <button onClick={() => { setShowReset(true); setResetEmail(email); }} className="text-xs text-emerald-500 hover:text-emerald-600 font-medium" data-testid="doctor-forgot-password">Password dimenticata?</button>
          <br />
          <Link to="/login" className="text-xs text-slate-400 hover:text-slate-600">Accesso staff</Link>
        </div>

        {showReset && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-6 w-full max-w-md" data-testid="doctor-reset-modal">
              <h3 className="font-semibold text-lg mb-4">Reset Password</h3>
              {resetSent ? (
                <div>
                  <p className="text-sm text-emerald-600 mb-4">{"Se l'email esiste, riceverai le istruzioni per il reset."}</p>
                  <Button onClick={() => { setShowReset(false); setResetSent(false); }} className="w-full">Chiudi</Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <Input type="email" placeholder="La tua email" value={resetEmail} onChange={e => setResetEmail(e.target.value)} data-testid="doctor-reset-email" />
                  <Button className="w-full bg-emerald-500 hover:bg-emerald-600" onClick={async () => {
                    await fetch(`${API}/api/auth/reset-password`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: resetEmail }) });
                    setResetSent(true);
                  }} data-testid="doctor-reset-submit">Invia email di reset</Button>
                  <Button variant="ghost" className="w-full" onClick={() => setShowReset(false)}>Annulla</Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== CALENDAR - MULTI VIEW ====================
const HOURS = Array.from({ length: 12 }, (_, i) => i + 8); // 08:00 - 19:00

function DoctorCalendar() {
  const [view, setView] = useState(() => localStorage.getItem('doc_cal_view') || 'day');
  const [currentDate, setCurrentDate] = useState(new Date().toISOString().split('T')[0]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAppt, setSelectedAppt] = useState(null);

  const changeView = (v) => { setView(v); localStorage.setItem('doc_cal_view', v); };

  // Navigation
  const navigate = (delta) => {
    const d = new Date(currentDate + 'T00:00:00');
    if (view === 'day') d.setDate(d.getDate() + delta);
    else if (view === 'week') d.setDate(d.getDate() + delta * 7);
    else d.setMonth(d.getMonth() + delta);
    setCurrentDate(d.toISOString().split('T')[0]);
  };
  const goToday = () => setCurrentDate(new Date().toISOString().split('T')[0]);

  // Fetch data based on view
  useEffect(() => {
    setLoading(true);
    const fetch = async () => {
      try {
        let res;
        if (view === 'day') {
          res = await doctorApi().get(`/agenda?date=${currentDate}`);
        } else if (view === 'week') {
          const d = new Date(currentDate + 'T00:00:00');
          const start = new Date(d);
          start.setDate(d.getDate() - d.getDay() + 1); // Monday
          const dates = Array.from({ length: 7 }, (_, i) => {
            const dd = new Date(start);
            dd.setDate(start.getDate() + i);
            return dd.toISOString().split('T')[0];
          });
          // Fetch all 7 days
          const promises = dates.map(dt => doctorApi().get(`/agenda?date=${dt}`).then(r => r.data.map(a => ({ ...a, date: dt }))).catch(() => []));
          const results = await Promise.all(promises);
          res = { data: results.flat() };
        } else {
          const month = currentDate.substring(0, 7);
          res = await doctorApi().get(`/month?month=${month}`);
        }
        setAppointments(res.data || []);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetch();
  }, [currentDate, view]);

  // Header label
  const headerLabel = () => {
    const d = new Date(currentDate + 'T00:00:00');
    if (view === 'day') {
      const today = new Date().toISOString().split('T')[0];
      const prefix = currentDate === today ? 'Oggi — ' : '';
      return prefix + d.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' });
    }
    if (view === 'week') {
      const start = new Date(d);
      start.setDate(d.getDate() - d.getDay() + 1);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return `${start.getDate()} - ${end.getDate()} ${end.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}`;
    }
    return d.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
  };

  // Week days for week view
  const getWeekDays = () => {
    const d = new Date(currentDate + 'T00:00:00');
    const start = new Date(d);
    start.setDate(d.getDate() - d.getDay() + 1);
    return Array.from({ length: 7 }, (_, i) => {
      const dd = new Date(start);
      dd.setDate(start.getDate() + i);
      return dd.toISOString().split('T')[0];
    });
  };

  // Month grid
  const getMonthGrid = () => {
    const d = new Date(currentDate.substring(0, 7) + '-01T00:00:00');
    const year = d.getFullYear(), month = d.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const startOffset = firstDay === 0 ? 6 : firstDay - 1; // Monday start
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startOffset; i++) cells.push(null);
    for (let i = 1; i <= daysInMonth; i++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
      cells.push(dateStr);
    }
    return cells;
  };

  const apptColor = (status) => {
    if (status === 'confirmed') return 'bg-emerald-500';
    if (status === 'cancelled') return 'bg-red-400';
    return 'bg-sky-500';
  };

  const apptBorder = (status) => {
    if (status === 'confirmed') return 'border-l-emerald-500 bg-emerald-50';
    if (status === 'cancelled') return 'border-l-red-400 bg-red-50';
    return 'border-l-sky-500 bg-sky-50';
  };

  return (
    <DoctorShell title="Calendario">
      <div className="space-y-3" data-testid="doctor-calendar">
        {/* View switcher */}
        <div className="flex bg-white border border-slate-200 rounded-lg p-1 gap-0.5">
          {[['day', 'Giorno'], ['week', 'Settimana'], ['month', 'Mese']].map(([key, label]) => (
            <button key={key} onClick={() => changeView(key)}
              className={`flex-1 py-1.5 text-xs font-semibold rounded transition-all ${view === key ? 'bg-emerald-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'}`}
              data-testid={`view-${key}`}>
              {label}
            </button>
          ))}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between bg-white border border-slate-200 rounded-xl px-3 py-2">
          <button onClick={() => navigate(-1)} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors" data-testid="cal-prev">
            <ChevronLeft className="w-4 h-4 text-slate-600" />
          </button>
          <div className="flex items-center gap-2">
            <p className="font-semibold text-slate-800 text-sm capitalize">{headerLabel()}</p>
            {currentDate !== new Date().toISOString().split('T')[0] && (
              <button onClick={goToday} className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium hover:bg-emerald-200 transition-colors" data-testid="cal-today">
                Oggi
              </button>
            )}
          </div>
          <button onClick={() => navigate(1)} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors" data-testid="cal-next">
            <ChevronRight className="w-4 h-4 text-slate-600" />
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-500" /></div>
        ) : (
          <>
            {/* ===== DAY VIEW ===== */}
            {view === 'day' && (
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="day-view">
                {HOURS.map(hour => {
                  const hourStr = `${String(hour).padStart(2, '0')}:`;
                  const hourAppts = appointments.filter(a => a.time && a.time.startsWith(hourStr));
                  return (
                    <div key={hour} className="flex border-b border-slate-100 last:border-0 min-h-[52px]">
                      <div className="w-14 flex-shrink-0 py-2 px-2 text-right">
                        <span className="text-[11px] font-mono text-slate-400">{String(hour).padStart(2, '0')}:00</span>
                      </div>
                      <div className="flex-1 py-1 px-1.5 space-y-0.5">
                        {hourAppts.map(a => (
                          <button key={a.id} onClick={() => setSelectedAppt(a)}
                            className={`w-full text-left rounded-md px-2 py-1.5 border-l-[3px] transition-all hover:shadow-sm ${apptBorder(a.status)}`}
                            data-testid={`appt-${a.id}`}>
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-semibold text-slate-800">{a.time} — {a.patient_name || 'Paziente'}</span>
                              <span className={`w-1.5 h-1.5 rounded-full ${apptColor(a.status)}`} />
                            </div>
                            <span className="text-[11px] text-slate-500">{a.service}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
                {appointments.length === 0 && (
                  <div className="py-12 text-center">
                    <Calendar className="w-8 h-8 text-slate-200 mx-auto mb-2" />
                    <p className="text-slate-400 text-sm">Nessun appuntamento</p>
                  </div>
                )}
              </div>
            )}

            {/* ===== WEEK VIEW ===== */}
            {view === 'week' && (
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="week-view">
                {/* Day headers */}
                <div className="grid grid-cols-8 border-b border-slate-200 bg-slate-50">
                  <div className="w-10" />
                  {getWeekDays().map(dateStr => {
                    const d = new Date(dateStr + 'T00:00:00');
                    const isToday = dateStr === new Date().toISOString().split('T')[0];
                    return (
                      <div key={dateStr} className={`py-2 text-center border-l border-slate-200 ${isToday ? 'bg-emerald-50' : ''}`}>
                        <p className="text-[10px] text-slate-400 uppercase">{d.toLocaleDateString('it-IT', { weekday: 'short' })}</p>
                        <p className={`text-sm font-bold ${isToday ? 'text-emerald-600' : 'text-slate-700'}`}>{d.getDate()}</p>
                      </div>
                    );
                  })}
                </div>
                {/* Hour rows */}
                <div className="overflow-y-auto" style={{ maxHeight: '420px' }}>
                  {HOURS.map(hour => {
                    const hourStr = `${String(hour).padStart(2, '0')}:`;
                    return (
                      <div key={hour} className="grid grid-cols-8 border-b border-slate-50 min-h-[40px]">
                        <div className="w-10 py-1 pr-1 text-right flex-shrink-0">
                          <span className="text-[9px] font-mono text-slate-300">{String(hour).padStart(2, '0')}</span>
                        </div>
                        {getWeekDays().map(dateStr => {
                          const dayAppts = appointments.filter(a => a.date === dateStr && a.time && a.time.startsWith(hourStr));
                          return (
                            <div key={dateStr} className="border-l border-slate-100 px-0.5 py-0.5">
                              {dayAppts.map(a => (
                                <button key={a.id} onClick={() => setSelectedAppt(a)}
                                  className={`w-full text-left rounded px-1 py-0.5 mb-0.5 ${apptColor(a.status)} bg-opacity-15 hover:bg-opacity-25 transition-all`}
                                  data-testid={`week-appt-${a.id}`}>
                                  <p className="text-[9px] font-semibold text-slate-700 truncate">{a.time}</p>
                                  <p className="text-[9px] text-slate-600 truncate">{a.patient_name}</p>
                                </button>
                              ))}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ===== MONTH VIEW ===== */}
            {view === 'month' && (
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="month-view">
                <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-200">
                  {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map(d => (
                    <div key={d} className="py-2 text-center text-[10px] font-semibold text-slate-400 uppercase">{d}</div>
                  ))}
                </div>
                <div className="grid grid-cols-7">
                  {getMonthGrid().map((dateStr, i) => {
                    if (!dateStr) return <div key={`empty-${i}`} className="min-h-[60px] border-b border-r border-slate-50 bg-slate-25" />;
                    const dayAppts = appointments.filter(a => a.date === dateStr);
                    const isToday = dateStr === new Date().toISOString().split('T')[0];
                    const dayNum = parseInt(dateStr.split('-')[2]);
                    return (
                      <button key={dateStr} onClick={() => { setCurrentDate(dateStr); changeView('day'); }}
                        className={`min-h-[60px] border-b border-r border-slate-100 p-1 text-left hover:bg-slate-50 transition-colors ${isToday ? 'bg-emerald-50' : ''}`}
                        data-testid={`month-day-${dateStr}`}>
                        <p className={`text-xs font-medium mb-0.5 ${isToday ? 'text-emerald-600 font-bold' : 'text-slate-600'}`}>{dayNum}</p>
                        {dayAppts.length > 0 && (
                          <div className="space-y-0.5">
                            {dayAppts.slice(0, 2).map(a => (
                              <div key={a.id} className={`rounded px-1 py-0.5 ${apptColor(a.status)} bg-opacity-20`}>
                                <p className="text-[8px] font-medium text-slate-700 truncate">{a.time} {a.patient_name}</p>
                              </div>
                            ))}
                            {dayAppts.length > 2 && (
                              <p className="text-[8px] text-slate-400 text-center">+{dayAppts.length - 2} altri</p>
                            )}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Stats footer */}
            <p className="text-center text-xs text-slate-400">
              {appointments.length} appuntament{appointments.length === 1 ? 'o' : 'i'}
              {view === 'day' ? ' oggi' : view === 'week' ? ' questa settimana' : ' questo mese'}
            </p>
          </>
        )}

        {/* Appointment detail modal */}
        {selectedAppt && (
          <div className="fixed inset-0 bg-black/40 flex items-end z-50" onClick={() => setSelectedAppt(null)}>
            <div className="bg-white rounded-t-2xl w-full max-w-lg mx-auto p-5 space-y-3" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-slate-800">Dettaglio Appuntamento</h3>
                <button onClick={() => setSelectedAppt(null)} className="text-slate-400"><X className="w-5 h-5" /></button>
              </div>
              <div className="bg-slate-50 rounded-xl p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-xs text-slate-500">Paziente</span>
                  <span className="text-sm font-medium text-slate-800">{selectedAppt.patient_name || '-'}</span>
                </div>
                <div className="flex justify-between border-t border-slate-200 pt-2">
                  <span className="text-xs text-slate-500">Data</span>
                  <span className="text-sm text-slate-800">{selectedAppt.date ? new Date(selectedAppt.date + 'T00:00:00').toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' }) : '-'}</span>
                </div>
                <div className="flex justify-between border-t border-slate-200 pt-2">
                  <span className="text-xs text-slate-500">Orario</span>
                  <span className="text-sm font-semibold text-emerald-600">{selectedAppt.time}</span>
                </div>
                <div className="flex justify-between border-t border-slate-200 pt-2">
                  <span className="text-xs text-slate-500">Prestazione</span>
                  <span className="text-sm text-slate-800">{selectedAppt.service || '-'}</span>
                </div>
                <div className="flex justify-between border-t border-slate-200 pt-2">
                  <span className="text-xs text-slate-500">Stato</span>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                    selectedAppt.status === 'confirmed' ? 'bg-emerald-100 text-emerald-700' :
                    selectedAppt.status === 'cancelled' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'
                  }`}>
                    {selectedAppt.status === 'confirmed' ? 'Confermato' : selectedAppt.status === 'cancelled' ? 'Cancellato' : selectedAppt.status}
                  </span>
                </div>
                {selectedAppt.patient_phone && (
                  <div className="flex justify-between border-t border-slate-200 pt-2">
                    <span className="text-xs text-slate-500">Telefono</span>
                    <a href={`tel:${selectedAppt.patient_phone}`} className="text-sm text-emerald-600 font-medium flex items-center gap-1">
                      <Phone className="w-3 h-3" />{selectedAppt.patient_phone}
                    </a>
                  </div>
                )}
                {selectedAppt.notes && (
                  <div className="border-t border-slate-200 pt-2">
                    <span className="text-xs text-slate-500">Note</span>
                    <p className="text-sm text-slate-700 mt-0.5">{selectedAppt.notes}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </DoctorShell>
  );
}

// ==================== NAV BAR WITH BADGE ====================
function DoctorNavBar({ nav }) {
  const location = useLocation();
  const [pendingCount, setPendingCount] = useState(0);
  const [unreadChat, setUnreadChat] = useState(0);

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const [prescRes, chatRes] = await Promise.all([
          doctorApi().get('/prescriptions/pending-count').catch(() => ({ data: { count: 0 } })),
          doctorApi().get('/chat/unread-count').catch(() => ({ data: { count: 0 } })),
        ]);
        setPendingCount(prescRes.data.count || 0);
        setUnreadChat(chatRes.data.count || 0);
      } catch (e) { /* ignore */ }
    };
    fetchCounts();
    const interval = setInterval(fetchCounts, 15000);
    return () => clearInterval(interval);
  }, []);

  return (
    <nav className="bg-white border-t border-slate-200 flex sticky bottom-0">
      {nav.map(item => {
        const active = location.pathname === item.to;
        return (
          <Link key={item.to} to={item.to} className={`flex-1 flex flex-col items-center py-2.5 text-xs transition-colors relative ${active ? 'text-sky-600' : 'text-slate-400'}`} data-testid={`nav-${item.label.toLowerCase()}`}>
            <item.icon className="w-5 h-5 mb-0.5" />
            {item.label}
            {item.label === 'Ricette' && pendingCount > 0 && (
              <span className="absolute top-1 right-1/4 bg-red-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center" data-testid="prescription-badge">{pendingCount}</span>
            )}
            {item.label === 'Chat' && unreadChat > 0 && (
              <span className="absolute top-1 right-1/4 bg-sky-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center" data-testid="chat-badge">{unreadChat}</span>
            )}
          </Link>
        );
      })}
    </nav>
  );
}

// ==================== DOCTOR PRESCRIPTIONS ====================
function DoctorPrescriptions() {
  const { doctor } = useDoctorAuth();
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [uploadModal, setUploadModal] = useState(null);
  const [patients, setPatients] = useState([]);
  const [directUpload, setDirectUpload] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState('');
  const [uploadNotes, setUploadNotes] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [denyId, setDenyId] = useState(null);
  const [denyReason, setDenyReason] = useState('');
  const fileRef = useRef(null);
  const directFileRef = useRef(null);

  const fetchPrescriptions = async () => {
    try {
      const res = await doctorApi().get('/prescriptions');
      setPrescriptions(res.data || []);
    } catch (e) { toast.error('Errore caricamento ricette'); }
    setLoading(false);
  };

  const fetchPatients = async () => {
    try {
      const token = localStorage.getItem('doctor_token');
      const res = await axios.get(`${API}/api/patients`, { headers: { Authorization: `Bearer ${token}` } });
      setPatients(res.data || []);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { fetchPrescriptions(); fetchPatients(); }, []);

  const filtered = prescriptions.filter(p => {
    if (filter === 'pending') return p.status === 'pending';
    if (filter === 'approved') return p.status === 'approved';
    if (filter === 'denied') return p.status === 'denied';
    return true;
  });

  const handleUpload = async (prescriptionId) => {
    if (!uploadFile) return toast.error('Seleziona un file');
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      await doctorApi().post(`/prescriptions/${prescriptionId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        baseURL: `${API}/api`,
      });
      toast.success('Ricetta caricata e inviata al paziente!');
      setUploadModal(null);
      setUploadFile(null);
      fetchPrescriptions();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore upload'); }
    setUploading(false);
  };

  const handleDirectUpload = async () => {
    if (!uploadFile || !selectedPatient) return toast.error('Seleziona paziente e file');
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('patient_id', selectedPatient);
      formData.append('notes', uploadNotes);
      const token = localStorage.getItem('doctor_token');
      await axios.post(`${API}/api/prescriptions/upload-direct`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Ricetta caricata e inviata al paziente!');
      setDirectUpload(false);
      setUploadFile(null);
      setSelectedPatient('');
      setUploadNotes('');
      fetchPrescriptions();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore upload'); }
    setUploading(false);
  };

  const handleDeny = async () => {
    if (!denyId) return;
    try {
      const token = localStorage.getItem('doctor_token');
      await axios.put(`${API}/api/prescriptions/${denyId}/deny`, { reason: denyReason }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Richiesta rifiutata');
      setDenyId(null);
      setDenyReason('');
      fetchPrescriptions();
    } catch (e) { toast.error('Errore'); }
  };

  return (
    <DoctorShell title="Ricette">
      <div className="space-y-4" data-testid="doctor-prescriptions">
        {/* Actions */}
        <div className="flex gap-2">
          <Button onClick={() => setDirectUpload(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm" data-testid="upload-prescription-btn">
            <Upload className="w-4 h-4 mr-1.5" /> Carica Ricetta
          </Button>
        </div>

        {/* Filter tabs */}
        <div className="flex bg-white border border-slate-200 rounded-lg p-1 gap-1">
          {[['pending', 'In attesa'], ['approved', 'Approvate'], ['denied', 'Rifiutate'], ['all', 'Tutte']].map(([key, label]) => (
            <button key={key} onClick={() => setFilter(key)}
              className={`flex-1 py-1.5 text-xs font-medium rounded transition-colors ${filter === key ? 'bg-sky-100 text-sky-700' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid={`filter-${key}`}>
              {label} {key === 'pending' && prescriptions.filter(p => p.status === 'pending').length > 0 && (
                <span className="ml-1 bg-red-500 text-white text-[10px] rounded-full px-1.5">{prescriptions.filter(p => p.status === 'pending').length}</span>
              )}
            </button>
          ))}
        </div>

        {/* List */}
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : filtered.length === 0 ? (
          <p className="text-center text-slate-400 text-sm py-8">Nessuna ricetta</p>
        ) : (
          <div className="space-y-2">
            {filtered.map(p => (
              <div key={p.id} className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`prescription-${p.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="font-medium text-slate-800 text-sm">{p.patient_name || 'Paziente'}</p>
                    <p className="text-xs text-slate-400">{p.created_at ? new Date(p.created_at).toLocaleDateString('it-IT') : ''}</p>
                    {p.requested_by_role === 'patient' && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">Richiesta paziente</span>}
                  </div>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                    p.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                    p.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {p.status === 'pending' ? 'In attesa' : p.status === 'approved' ? 'Approvata' : 'Rifiutata'}
                  </span>
                </div>
                {p.medications?.length > 0 && (
                  <div className="text-xs text-slate-600 mb-2">
                    <span className="font-medium">Farmaci: </span>
                    {p.medications.map((m, i) => <span key={i}>{typeof m === 'string' ? m : m.name}{i < p.medications.length - 1 ? ', ' : ''}</span>)}
                  </div>
                )}
                {p.notes && <p className="text-xs text-slate-500 mb-2">{p.notes}</p>}
                {p.has_file && <p className="text-xs text-emerald-600 flex items-center gap-1"><FileText className="w-3 h-3" /> {p.file_name}</p>}

                {p.status === 'pending' && (
                  <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100">
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs flex-1"
                      onClick={() => { setUploadModal(p.id); setUploadFile(null); }}
                      data-testid={`approve-${p.id}`}>
                      <Check className="w-3 h-3 mr-1" /> Approva e Carica
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 text-xs"
                      onClick={() => { setDenyId(p.id); setDenyReason(''); }}
                      data-testid={`deny-${p.id}`}>
                      <XCircle className="w-3 h-3 mr-1" /> Rifiuta
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Upload modal for existing prescription */}
        {uploadModal && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl w-full max-w-sm p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-slate-800">Carica Ricetta</h3>
                <button onClick={() => setUploadModal(null)} className="text-slate-400"><X className="w-5 h-5" /></button>
              </div>
              <div>
                <Label>File Ricetta (PDF, immagine)</Label>
                <input type="file" ref={fileRef} accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
                  onChange={(e) => setUploadFile(e.target.files[0])} className="mt-1.5 w-full text-sm" data-testid="upload-file-input" />
              </div>
              {uploadFile && <p className="text-xs text-emerald-600">{uploadFile.name} ({(uploadFile.size / 1024).toFixed(0)} KB)</p>}
              <Button onClick={() => handleUpload(uploadModal)} disabled={uploading || !uploadFile}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="confirm-upload-btn">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Upload className="w-4 h-4 mr-1.5" />}
                {uploading ? 'Caricamento...' : 'Carica e Invia al Paziente'}
              </Button>
            </div>
          </div>
        )}

        {/* Direct upload modal */}
        {directUpload && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl w-full max-w-sm p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-slate-800">Carica Ricetta</h3>
                <button onClick={() => setDirectUpload(false)} className="text-slate-400"><X className="w-5 h-5" /></button>
              </div>
              <div>
                <Label>Paziente *</Label>
                <select value={selectedPatient} onChange={(e) => setSelectedPatient(e.target.value)}
                  className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="select-patient">
                  <option value="">Seleziona paziente...</option>
                  {patients.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <Label>Note (opzionale)</Label>
                <Input value={uploadNotes} onChange={(e) => setUploadNotes(e.target.value)} placeholder="Es. Rinnovo terapia" className="mt-1.5" />
              </div>
              <div>
                <Label>File Ricetta *</Label>
                <input type="file" ref={directFileRef} accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
                  onChange={(e) => setUploadFile(e.target.files[0])} className="mt-1.5 w-full text-sm" data-testid="direct-upload-file" />
              </div>
              {uploadFile && <p className="text-xs text-emerald-600">{uploadFile.name} ({(uploadFile.size / 1024).toFixed(0)} KB)</p>}
              <Button onClick={handleDirectUpload} disabled={uploading || !uploadFile || !selectedPatient}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="confirm-direct-upload-btn">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Upload className="w-4 h-4 mr-1.5" />}
                {uploading ? 'Caricamento...' : 'Carica e Invia al Paziente'}
              </Button>
            </div>
          </div>
        )}

        {/* Deny modal */}
        {denyId && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl w-full max-w-sm p-5 space-y-4">
              <h3 className="font-semibold text-slate-800">Rifiuta Richiesta</h3>
              <div>
                <Label>Motivazione (opzionale)</Label>
                <Input value={denyReason} onChange={(e) => setDenyReason(e.target.value)} placeholder="Es. Necessaria visita di controllo" className="mt-1.5" />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setDenyId(null)} className="flex-1">Annulla</Button>
                <Button onClick={handleDeny} className="flex-1 bg-red-600 hover:bg-red-700 text-white">Rifiuta</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DoctorShell>
  );
}

// ==================== DOCTOR CHAT ====================
function DoctorChat() {
  const { doctor } = useDoctorAuth();
  const token = localStorage.getItem('doctor_token');

  return (
    <DoctorShell title="Chat">
      <ClinicChat
        apiBase="/api/portal/doctor/chat"
        getHeaders={() => ({ Authorization: `Bearer ${token}` })}
        currentUserId={doctor?.id || ''}
        currentUserName={`Dr. ${doctor?.name || ''}`}
      />
    </DoctorShell>
  );
}

// ==================== PROFILE ====================
function DoctorProfile() {
  const { doctor } = useDoctorAuth();
  const [twoFaEnabled, setTwoFaEnabled] = useState(false);
  const [setupMode, setSetupMode] = useState(false);
  const [verifyCode, setVerifyCode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [loading2fa, setLoading2fa] = useState(false);

  useEffect(() => {
    // Check 2FA status
    doctorApi().get('/me').then(res => {
      setTwoFaEnabled(res.data?.two_factor_enabled || false);
    }).catch(() => {});
  }, []);

  const handleSetup2fa = async () => {
    setLoading2fa(true);
    try {
      await doctorApi().post('/2fa/setup', { method: 'email' }, { baseURL: `${API}/api/portal/doctor` });
      setSetupMode(true);
      toast.success('Codice inviato alla tua email!');
    } catch (e) { toast.error('Errore'); }
    setLoading2fa(false);
  };

  const handleVerify2fa = async () => {
    setLoading2fa(true);
    try {
      await doctorApi().post('/2fa/verify-setup', { code: verifyCode }, { baseURL: `${API}/api/portal/doctor` });
      setTwoFaEnabled(true);
      setSetupMode(false);
      setVerifyCode('');
      toast.success('2FA attivata!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Codice non valido'); }
    setLoading2fa(false);
  };

  const handleDisable2fa = async () => {
    if (!disablePassword) return toast.error('Inserisci la password');
    setLoading2fa(true);
    try {
      await doctorApi().post('/2fa/disable', { password: disablePassword }, { baseURL: `${API}/api/portal/doctor` });
      setTwoFaEnabled(false);
      setDisablePassword('');
      toast.success('2FA disattivata');
    } catch (e) { toast.error(e.response?.data?.detail || 'Password errata'); }
    setLoading2fa(false);
  };

  return (
    <DoctorShell title="Profilo">
      <div className="space-y-4" data-testid="doctor-profile">
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center">
              <Stethoscope className="w-7 h-7 text-emerald-600" />
            </div>
            <div>
              <p className="font-semibold text-slate-800">Dr. {doctor?.name}</p>
              <p className="text-sm text-slate-500">{doctor?.specialization}</p>
              <p className="text-xs text-slate-400 mt-0.5">{doctor?.email}</p>
            </div>
          </div>
        </div>

        {/* 2FA Section */}
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="doctor-2fa-section">
          <div className="flex items-center gap-2 mb-3">
            <Shield className="w-5 h-5 text-emerald-600" />
            <h3 className="font-semibold text-slate-800 text-sm">Autenticazione a Due Fattori (2FA)</h3>
          </div>

          {twoFaEnabled ? (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="w-2 h-2 bg-emerald-500 rounded-full" />
                <span className="text-sm text-emerald-700 font-medium">2FA Attiva</span>
              </div>
              <p className="text-xs text-slate-500 mb-3">Per disattivare, inserisci la tua password:</p>
              <div className="flex gap-2">
                <Input type="password" value={disablePassword} onChange={e => setDisablePassword(e.target.value)}
                  placeholder="Password" className="text-sm" data-testid="disable-2fa-password" />
                <Button onClick={handleDisable2fa} variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 text-xs shrink-0"
                  disabled={loading2fa} data-testid="disable-2fa-btn">
                  Disattiva
                </Button>
              </div>
            </div>
          ) : setupMode ? (
            <div>
              <p className="text-sm text-slate-600 mb-3">Inserisci il codice a 6 cifre ricevuto via email:</p>
              <div className="flex gap-2">
                <Input type="text" inputMode="numeric" maxLength={6} value={verifyCode}
                  onChange={e => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000" className="text-center text-lg tracking-[0.2em] font-mono" data-testid="setup-2fa-code" />
                <Button onClick={handleVerify2fa} className="bg-emerald-500 hover:bg-emerald-600 text-white text-xs shrink-0"
                  disabled={loading2fa || verifyCode.length < 6} data-testid="verify-2fa-btn">
                  Verifica
                </Button>
              </div>
              <button onClick={() => setSetupMode(false)} className="text-xs text-slate-400 mt-2">Annulla</button>
            </div>
          ) : (
            <div>
              <p className="text-xs text-slate-500 mb-3">
                Proteggi il tuo account con un codice di verifica via email ad ogni accesso.
              </p>
              <Button onClick={handleSetup2fa} className="bg-emerald-500 hover:bg-emerald-600 text-white text-sm"
                disabled={loading2fa} data-testid="enable-2fa-btn">
                <Shield className="w-4 h-4 mr-1.5" />
                {loading2fa ? 'Invio codice...' : 'Attiva 2FA'}
              </Button>
            </div>
          )}
        </div>

        <div className="text-center pt-4">
          <p className="text-xs text-slate-400">VoiceBook by TelcaVoIP AI</p>
        </div>
      </div>
    </DoctorShell>
  );
}

// ==================== MAIN DOCTOR APP ====================
export default function DoctorPortal() {
  return (
    <DoctorAuthProvider>
      <Toaster position="top-center" richColors />
      <Routes>
        <Route path="login" element={<DoctorLoginPage />} />
        <Route path="" element={<RequireDoctorAuth><DoctorCalendar /></RequireDoctorAuth>} />
        <Route path="ricette" element={<RequireDoctorAuth><DoctorPrescriptions /></RequireDoctorAuth>} />
        <Route path="chat" element={<RequireDoctorAuth><DoctorChat /></RequireDoctorAuth>} />
        <Route path="profilo" element={<RequireDoctorAuth><DoctorProfile /></RequireDoctorAuth>} />
        <Route path="settimana" element={<Navigate to="/medico" replace />} />
        <Route path="*" element={<Navigate to="/medico" replace />} />
      </Routes>
    </DoctorAuthProvider>
  );
}

function RequireDoctorAuth({ children }) {
  const { isAuthenticated, loading } = useDoctorAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>;
  if (!isAuthenticated) return <Navigate to="/medico/login" replace />;
  return children;
}
